import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Form, notification } from 'antd';
import { debounce } from 'lodash-es';
import {
  useClientApi,
  ClientSearchResult,
  ClientSupplier,
  ClientSupplierVendor,
  DedupConflictResponse,
  CreateAndMapVendorResponse,
  BulkMatchNeedsReview
} from 'api/useClient';
import { useVendorApi, VendorMultiModeSearchResult, SimKeySearchResponse } from 'api/vendor';
import type { SimKeyMatchCandidate } from 'api/vendor';
import type { RefSelectProps } from 'antd/lib/select';
import type {
  BulkMatchQueueItem,
  BulkMatchSummary,
  CreateVendorFormValues,
  DedupCandidate,
  LocalVendorOption,
  UseSupplierMappingReturn,
  VendorSearchOption
} from '../types';

// ─── Private helpers ──────────────────────────────────────────────────────────

/**
 * Type guard — returns `true` when `value` matches the {@link VendorSearchOption} shape.
 * @param {unknown} value - Candidate value to test.
 * @returns {boolean} `true` if `value` has a numeric `id` and a `name` property.
 */
const isVendorSearchOption = (value: unknown): value is VendorSearchOption => {
  if (typeof value !== 'object' || value === null) return false;
  const record = value as Record<string, unknown>;
  return typeof record.id === 'number' && 'name' in record;
};

/**
 * Strips protocol, `www.` prefix, and trailing whitespace from a domain string.
 * @param {string} domain - Raw domain value entered by the user.
 * @returns {string} Normalised lowercase domain (e.g. `"acme.com"`).
 */
const normalizeDomain = (domain: string): string =>
  domain
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .toLowerCase();

/**
 * Converts a React node to a plain string for display or parsing purposes.
 * @param {ReactNode} node - Node extracted from a Select option label.
 * @returns {string} String representation, or `""` for non-primitive nodes.
 */
const getNodeText = (node: ReactNode): string => {
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return node.toString();
  return '';
};

/**
 * Returns an incremented session ID value.
 * Used to reset VendorsSearch between map-modal openings by changing its React key.
 * @param {number} prev - Current session ID.
 * @returns {number} Next session ID.
 */
const nextSessionId = (prev: number): number => prev + 1;

/**
 * Converts a BulkMatchNeedsReview item into a BulkMatchQueueItem by transforming
 * the candidates into the SimKeySearchResponse shape the MapVendorModal expects.
 * @param item
 */
const toBulkMatchQueueItem = (item: BulkMatchNeedsReview): BulkMatchQueueItem => {
  const exactCandidate = item.candidates.find((c) => c.distance === 0) ?? null;
  const fuzzyMatches = item.candidates
    .filter((c) => c.distance > 0)
    .map((c) => ({ ...c, confidence: c.confidence as SimKeyMatchCandidate['confidence'] }));

  return {
    supplierId: item.supplierId,
    supplierExternalId: item.supplierExternalId,
    supplierName: item.supplierName,
    simKeyResults: {
      inputSimKey: '',
      exactMatch: exactCandidate ? { ...exactCandidate, confidence: 'exact' as const } : null,
      fuzzyMatches
    }
  };
};

/**
 * Type guard for the DedupConflictResponse shape.
 * @param res
 */
const isDedupConflict = (
  res: CreateAndMapVendorResponse | DedupConflictResponse | undefined
): res is DedupConflictResponse => {
  return !!res && 'candidates' in res && Array.isArray((res as DedupConflictResponse).candidates);
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Manages all state and side-effects for the Supplier Mapping feature page.
 *
 * Responsibilities:
 * - Client search with debouncing and result management
 * - Supplier list fetching and local vendor override tracking
 * - Map-vendor modal lifecycle with sim_key auto-suggestion
 * - Create-vendor modal lifecycle with dedup confirmation flow
 * - Vendor creation via API and immediate optimistic mapping
 * - Unmapped-only filter with localStorage persistence
 * @returns {UseSupplierMappingReturn} All state values, derived data, and handlers needed by child components.
 */
export const useSupplierMapping = (): UseSupplierMappingReturn => {
  // ── Client search ────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<ClientSearchResult[]>([]);
  const [selectedClient, setSelectedClient] = useState<ClientSearchResult | undefined>();

  // ── Supplier table ───────────────────────────────────────────────────────
  const [suppliers, setSuppliers] = useState<ClientSupplier[]>([]);
  const [suppliersLoading, setSuppliersLoading] = useState(false);
  const [localVendorOverrides, setLocalVendorOverrides] = useState<Record<number, ClientSupplierVendor | null>>({});
  const [localCreatedVendors, setLocalCreatedVendors] = useState<LocalVendorOption[]>([]);
  const [showUnmappedOnly, setShowUnmappedOnly] = useState(false);

  // ── Map vendor modal ─────────────────────────────────────────────────────
  const [isMapModalOpen, setIsMapModalOpen] = useState(false);
  const [activeSupplier, setActiveSupplier] = useState<ClientSupplier | undefined>();
  const [mapModalSessionId, setMapModalSessionId] = useState(0);
  const mapSearchRef = useRef<RefSelectProps>(null);

  // ── Sim_key auto-suggestion ──────────────────────────────────────────────
  const [simKeySearchLoading, setSimKeySearchLoading] = useState(false);
  const [simKeyResults, setSimKeyResults] = useState<SimKeySearchResponse | null>(null);

  // ── Create vendor modal ──────────────────────────────────────────────────
  const [isCreateVendorModalOpen, setIsCreateVendorModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createVendorForm] = Form.useForm<CreateVendorFormValues>();
  const [dedupCandidates, setDedupCandidates] = useState<DedupCandidate[] | null>(null);

  // ── Stashed form values for dedup retry ──────────────────────────────────
  const [pendingCreateValues, setPendingCreateValues] = useState<CreateVendorFormValues | null>(null);

  // ── Bulk match ──────────────────────────────────────────────────────────
  const [isBulkMatching, setIsBulkMatching] = useState(false);
  const [bulkMatchSummary, setBulkMatchSummary] = useState<BulkMatchSummary | null>(null);
  const [bulkReviewQueue, setBulkReviewQueue] = useState<BulkMatchQueueItem[]>([]);
  const [bulkReviewIndex, setBulkReviewIndex] = useState(0);

  // ── API ──────────────────────────────────────────────────────────────────
  const {
    searchClientsByName,
    getClientSuppliers,
    upsertClientSupplier,
    deleteClientSupplier,
    createAndMapVendor,
    bulkMatchSuppliers
  } = useClientApi();
  const { searchVendorsMultiMode, searchVendorsBySimKey } = useVendorApi();

  // ── Auto-focus map modal search on open ──────────────────────────────────
  useEffect(() => {
    if (!isMapModalOpen) return;
    const timer = setTimeout(() => mapSearchRef.current?.focus(), 100);
    return () => clearTimeout(timer);
  }, [isMapModalOpen]);

  // ── Derived data ─────────────────────────────────────────────────────────

  /** Supplier rows with any in-session vendor overrides merged in, optionally filtered. */
  const displayedSuppliers = useMemo(() => {
    let result = suppliers.map((supplier) =>
      Object.prototype.hasOwnProperty.call(localVendorOverrides, supplier.id)
        ? { ...supplier, vendor: localVendorOverrides[supplier.id] }
        : supplier
    );
    if (showUnmappedOnly) {
      result = result.filter((s) => s.vendor === null);
    }
    return result;
  }, [localVendorOverrides, suppliers, showUnmappedOnly]);

  /** Select options built from vendors created during this session. */
  const defaultVendorOptions: VendorSearchOption[] = useMemo(
    () =>
      localCreatedVendors.map((vendor) => ({
        id: vendor.id,
        name: `${vendor.name}${vendor.domain ? ` • ${vendor.domain}` : ''}`
      })),
    [localCreatedVendors]
  );

  /** Display name of the supplier currently being mapped; used in modal heading copy. */
  const modalVendorTarget = activeSupplier?.supplierName ?? '';

  // ── Vendor search function for the map modal ─────────────────────────────

  /**
   * Searches vendors via the multi-mode endpoint and formats results for VendorsSearch.
   * @param {string} str - Search query string.
   * @returns {Promise<VendorSearchOption[]>} Formatted vendor options.
   */
  const vendorSearchFn = async (str: string): Promise<VendorSearchOption[]> => {
    const res = await searchVendorsMultiMode(str);
    return res.map((v: VendorMultiModeSearchResult) => ({
      id: v.id,
      name: v.domain ? `${v.name} • ${v.domain}` : v.name
    }));
  };

  // ── Private session helpers ───────────────────────────────────────────────

  const resetMapSession = (): void => {
    setMapModalSessionId(nextSessionId);
  };

  /**
   * Opens the map modal for a specific bulk review queue item by index.
   * @param queue
   * @param index
   */
  const openBulkReviewItem = (queue: BulkMatchQueueItem[], index: number): void => {
    const item = queue[index];
    if (!item) return;
    setActiveSupplier({
      id: item.supplierId,
      supplierId: item.supplierExternalId,
      supplierName: item.supplierName,
      vendor: null
    });
    setSimKeyResults(item.simKeyResults);
    setSimKeySearchLoading(false);
    setIsMapModalOpen(true);
    resetMapSession();
  };

  const applyMappedVendor = (vendor: ClientSupplierVendor): void => {
    if (!activeSupplier) return;
    setLocalVendorOverrides((prev) => ({ ...prev, [activeSupplier.id]: vendor }));
    setIsMapModalOpen(false);
    setIsCreateVendorModalOpen(false);
    setIsSubmitting(false);
    setActiveSupplier(undefined);
    setSimKeyResults(null);
    setSimKeySearchLoading(false);
    setDedupCandidates(null);
    setPendingCreateValues(null);
    createVendorForm.resetFields();
    resetMapSession();

    // Advance the bulk review queue if active
    if (bulkReviewQueue.length > 0 && bulkReviewIndex < bulkReviewQueue.length - 1) {
      const nextIndex = bulkReviewIndex + 1;
      setBulkReviewIndex(nextIndex);
      // Use setTimeout to let the current modal close before opening the next
      setTimeout(() => openBulkReviewItem(bulkReviewQueue, nextIndex), 200);
    } else if (bulkReviewQueue.length > 0) {
      // Queue exhausted
      setBulkReviewQueue([]);
      setBulkReviewIndex(0);
    }
  };

  // ── Supplier refresh ─────────────────────────────────────────────────────

  /**
   * Re-fetches the supplier list for the currently selected client.
   * Uses the refreshTrigger mechanism to ensure the fetch runs on a
   * fresh render cycle with a valid axios instance.
   */
  const refreshSuppliers = (): void => {
    setRefreshTrigger((prev) => prev + 1);
  };

  // ── Client search handlers ───────────────────────────────────────────────

  const handleSearch = debounce(async (name: string): Promise<void> => {
    if (name.length < 2) return;
    setLoading(true);
    const res = await searchClientsByName(name);
    if (Array.isArray(res)) setResults(res);
    setLoading(false);
  }, 325);

  const handleSelect = async (_id: number, option: ClientSearchResult): Promise<void> => {
    setSelectedClient(option);
    setSuppliers([]);
    setLocalVendorOverrides({});
    setSuppliersLoading(true);
    const res = await getClientSuppliers(option.id);
    if (Array.isArray(res)) setSuppliers(res);
    setSuppliersLoading(false);
  };

  const handleClear = (): void => {
    setResults([]);
    setSelectedClient(undefined);
    setSuppliers([]);
    setLocalVendorOverrides({});
    setLocalCreatedVendors([]);
    setIsMapModalOpen(false);
    setIsCreateVendorModalOpen(false);
    setActiveSupplier(undefined);
    setSimKeyResults(null);
    setSimKeySearchLoading(false);
    setDedupCandidates(null);
    setPendingCreateValues(null);
    createVendorForm.resetFields();
    resetMapSession();
  };

  // ── Supplier table handlers ───────────────────────────────────────────────

  const handleRemoveClientSupplier = async (supplier: ClientSupplier): Promise<void> => {
    if (!selectedClient) return;
    await deleteClientSupplier(selectedClient.id, supplier.id);
    setLocalVendorOverrides((prev) => ({ ...prev, [supplier.id]: null }));
  };

  const handleOpenMapVendorModal = (supplier: ClientSupplier): void => {
    setActiveSupplier(supplier);
    setIsCreateVendorModalOpen(false);
    setIsMapModalOpen(true);
    setDedupCandidates(null);
    resetMapSession();

    // Check if this supplier has pre-computed results from a bulk match
    const queueItem = bulkReviewQueue.find((item) => item.supplierId === supplier.id);
    if (queueItem) {
      // Use pre-computed results — no network call needed
      setSimKeyResults(queueItem.simKeyResults);
      setSimKeySearchLoading(false);
      setBulkReviewIndex(bulkReviewQueue.indexOf(queueItem));
      return;
    }

    // Fire sim_key auto-suggestion search
    setSimKeyResults(null);
    setSimKeySearchLoading(true);
    searchVendorsBySimKey(supplier.supplierName, supplier.supplierDomain)
      .then((res) => {
        setSimKeyResults(res);
      })
      .catch(() => {
        setSimKeyResults(null);
      })
      .finally(() => {
        setSimKeySearchLoading(false);
      });
  };

  const handleToggleUnmappedFilter = (checked: boolean): void => {
    // Switch component: checked = "show all", unchecked = "unmapped only"
    const unmappedOnly = !checked;
    setShowUnmappedOnly(unmappedOnly);
  };

  // ── Map vendor modal handlers ─────────────────────────────────────────────

  const handleCloseMapVendorModal = (): void => {
    setIsMapModalOpen(false);
    setActiveSupplier(undefined);
    setSimKeyResults(null);
    setSimKeySearchLoading(false);
    resetMapSession();
  };

  const handleSelectVendor = async (
    vendorId?: number,
    option?: VendorSearchOption | VendorSearchOption[]
  ): Promise<void> => {
    if (!vendorId || !activeSupplier || !selectedClient) return;

    // If the user chose a vendor they just created this session, use the cached data.
    const localVendor = localCreatedVendors.find((v) => v.id === vendorId);
    if (localVendor) {
      applyMappedVendor({ id: localVendor.id, name: localVendor.name, domain: localVendor.domain });
      return;
    }

    const selectedOption = Array.isArray(option)
      ? option.find((item) => item.id === vendorId)
      : option && isVendorSearchOption(option)
      ? option
      : undefined;

    const searchResults = await searchVendorsMultiMode(String(vendorId));
    const vendorDetails = searchResults.find((v: VendorMultiModeSearchResult) => v.id === vendorId);
    const optionText = selectedOption ? getNodeText(selectedOption.name) : '';
    const optionDomain = optionText.includes('•') ? optionText.split('•').at(1)?.trim() ?? null : null;

    const resolvedVendor: ClientSupplierVendor = {
      id: vendorId,
      name:
        typeof vendorDetails?.name === 'string' && vendorDetails.name.length
          ? vendorDetails.name
          : selectedOption
          ? optionText
              .split('•')[0]
              .replace(/\s*VND-[^ ]+$/, '')
              .trim()
          : 'Mapped Vendor',
      domain: typeof vendorDetails?.domain === 'string' ? vendorDetails.domain : optionDomain
    };

    setIsSubmitting(true);
    await upsertClientSupplier(selectedClient.id, {
      vendor_id: vendorId,
      supplier_id: activeSupplier.supplierId,
      supplier_name: activeSupplier.supplierName,
      supplier_domain: resolvedVendor.domain
    });

    applyMappedVendor(resolvedVendor);
  };

  // ── Sim_key match confirmation ────────────────────────────────────────────

  const handleConfirmSimKeyMatch = async (candidate: SimKeyMatchCandidate): Promise<void> => {
    if (!activeSupplier || !selectedClient) return;

    setIsSubmitting(true);
    await upsertClientSupplier(selectedClient.id, {
      vendor_id: candidate.id,
      supplier_id: activeSupplier.supplierId,
      supplier_name: activeSupplier.supplierName,
      supplier_domain: candidate.domain
    });

    applyMappedVendor({
      id: candidate.id,
      name: candidate.name,
      domain: candidate.domain
    });
  };

  // ── Create vendor modal handlers ──────────────────────────────────────────

  const handleOpenCreateVendorModal = (): void => {
    setIsMapModalOpen(false);
    setIsCreateVendorModalOpen(true);
    setDedupCandidates(null);
    setPendingCreateValues(null);
    createVendorForm.setFieldsValue({
      vendorName: activeSupplier?.supplierName ?? '',
      vendorDomain: ''
    });
  };

  const handleCancelCreateVendor = (): void => {
    setIsCreateVendorModalOpen(false);
    setIsMapModalOpen(true);
    setDedupCandidates(null);
    setPendingCreateValues(null);
    createVendorForm.resetFields();
    resetMapSession();
  };

  /**
   * Submits the create-and-map request. If the API returns a dedup conflict (409),
   * stores the candidates and stashes form values for a potential retry.
   * @param values
   */
  const handleCreateAndMapVendor = async (values: CreateVendorFormValues): Promise<void> => {
    if (!activeSupplier || !selectedClient) return;
    setIsSubmitting(true);
    setPendingCreateValues(values);

    const domain = values.vendorDomain ? normalizeDomain(values.vendorDomain) : null;

    const res = await createAndMapVendor(selectedClient.id, {
      vendorName: values.vendorName.trim(),
      vendorDomain: domain,
      dedupAcknowledged: false,
      supplier_id: activeSupplier.supplierId,
      supplier_name: activeSupplier.supplierName,
      supplier_domain: domain
    });

    if (isDedupConflict(res)) {
      // Show dedup confirmation UI
      setDedupCandidates(res.candidates);
      setIsSubmitting(false);
      return;
    }

    if (res && 'vendorId' in res) {
      setLocalCreatedVendors((prev) => [...prev, { id: res.vendorId, name: res.vendorName, domain: res.vendorDomain }]);

      applyMappedVendor({
        id: res.vendorId,
        name: res.vendorName,
        domain: res.vendorDomain
      });
    } else {
      setIsSubmitting(false);
    }
  };

  /**
   * Re-submits the create-and-map request with dedupAcknowledged: true.
   */
  const handleConfirmDedupCreate = (): void => {
    if (!activeSupplier || !selectedClient || !pendingCreateValues) return;
    setIsSubmitting(true);
    setDedupCandidates(null);

    const domain = pendingCreateValues.vendorDomain ? normalizeDomain(pendingCreateValues.vendorDomain) : null;

    createAndMapVendor(selectedClient.id, {
      vendorName: pendingCreateValues.vendorName.trim(),
      vendorDomain: domain,
      dedupAcknowledged: true,
      supplier_id: activeSupplier.supplierId,
      supplier_name: activeSupplier.supplierName,
      supplier_domain: domain
    }).then((res) => {
      if (res && 'vendorId' in res) {
        setLocalCreatedVendors((prev) => [
          ...prev,
          {
            id: (res as CreateAndMapVendorResponse).vendorId,
            name: (res as CreateAndMapVendorResponse).vendorName,
            domain: (res as CreateAndMapVendorResponse).vendorDomain
          }
        ]);

        applyMappedVendor({
          id: (res as CreateAndMapVendorResponse).vendorId,
          name: (res as CreateAndMapVendorResponse).vendorName,
          domain: (res as CreateAndMapVendorResponse).vendorDomain
        });
      } else {
        setIsSubmitting(false);
      }
    });
  };

  /**
   * Maps to an existing dedup candidate instead of creating a new vendor.
   * @param candidate
   */
  const handleUseDedupMatch = async (candidate: DedupCandidate): Promise<void> => {
    if (!activeSupplier || !selectedClient) return;

    setIsSubmitting(true);
    setDedupCandidates(null);

    await upsertClientSupplier(selectedClient.id, {
      vendor_id: candidate.id,
      supplier_id: activeSupplier.supplierId,
      supplier_name: activeSupplier.supplierName,
      supplier_domain: candidate.domain
    });

    applyMappedVendor({
      id: candidate.id,
      name: candidate.name,
      domain: candidate.domain
    });
  };

  // ── Bulk match handlers ────────────────────────────────────────────────────

  /**
   * When incremented, triggers a fresh supplier list fetch on the next
   * render cycle. The effect calls getClientSuppliers directly using
   * selectedClient.id (a stable value) so it always uses the current
   * render's axios instance.
   */
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    if (refreshTrigger === 0 || !selectedClient) return;
    setSuppliersLoading(true);
    setLocalVendorOverrides({});
    getClientSuppliers(selectedClient.id).then((res) => {
      if (Array.isArray(res)) setSuppliers(res);
      setSuppliersLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger]);

  const handleBulkMatch = async (): Promise<void> => {
    if (!selectedClient) return;
    setIsBulkMatching(true);
    setBulkMatchSummary(null);

    try {
      const res = await bulkMatchSuppliers(selectedClient.id);

      if (res) {
        setBulkMatchSummary({
          autoMappedCount: res.autoMappedCount,
          needsReviewCount: res.needsReview.length,
          noMatchCount: res.noMatch.length
        });

        const queue = res.needsReview.map(toBulkMatchQueueItem);
        setBulkReviewQueue(queue);
        setBulkReviewIndex(0);
      }
    } catch {
      notification.error({ message: 'Bulk match failed. Please try again.' });
    } finally {
      setIsBulkMatching(false);
      // Trigger refresh on next render cycle with a fresh axios instance
      setRefreshTrigger((prev) => prev + 1);
    }
  };

  const handleBulkReviewSkip = (): void => {
    setIsMapModalOpen(false);
    setActiveSupplier(undefined);
    setSimKeyResults(null);
    resetMapSession();

    if (bulkReviewQueue.length > 0 && bulkReviewIndex < bulkReviewQueue.length - 1) {
      const nextIndex = bulkReviewIndex + 1;
      setBulkReviewIndex(nextIndex);
      setTimeout(() => openBulkReviewItem(bulkReviewQueue, nextIndex), 200);
    } else {
      setBulkReviewQueue([]);
      setBulkReviewIndex(0);
    }
  };

  const handleDismissBulkSummary = (): void => {
    setBulkMatchSummary(null);
  };

  // ── Return ────────────────────────────────────────────────────────────────

  return {
    // Client search
    loading,
    results,
    selectedClient,
    handleSearch,
    handleSelect,
    handleClear,

    // Supplier table
    suppliersLoading,
    displayedSuppliers,
    handleRemoveClientSupplier,
    handleOpenMapVendorModal,
    showUnmappedOnly,
    handleToggleUnmappedFilter,
    handleRefreshSuppliers: refreshSuppliers,
    isBulkMatching,
    bulkMatchSummary,
    bulkReviewQueue,
    bulkReviewIndex,
    handleBulkMatch,
    handleBulkReviewSkip,
    handleDismissBulkSummary,

    // Map vendor modal
    isMapModalOpen,
    activeSupplier,
    mapModalSessionId,
    mapSearchRef,
    vendorSearchFn,
    defaultVendorOptions,
    handleCloseMapVendorModal,
    handleSelectVendor,
    handleOpenCreateVendorModal,
    simKeySearchLoading,
    simKeyResults,
    handleConfirmSimKeyMatch,

    // Create vendor modal
    isCreateVendorModalOpen,
    isSubmitting,
    createVendorForm,
    modalVendorTarget,
    handleCancelCreateVendor,
    handleCreateAndMapVendor,
    dedupCandidates,
    handleConfirmDedupCreate,
    handleUseDedupMatch
  };
};
